import { useEffect, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';

/**
 * Companion to auth0Config.ts (useRefreshTokensFallback: false).
 *
 * With the hidden-iframe fallback disabled, @auth0/auth0-spa-js no longer
 * calls its internal logout({ openUrl: false }) when the session dies —
 * getAccessTokenSilently just rejects (MissingRefreshTokenError /
 * invalid_grant) while isAuthenticated stays stale-true, leaving the user
 * "logged in" but locked out of every token-gated request. That stuck state
 * is why #875 (which first wired the fallback off) was reverted in #880.
 *
 * This guard revalidates the session on mount, on tab focus/visibility and on
 * a timer. When token acquisition fails with an UNRECOVERABLE auth error it
 * first tries to recover the session WITHOUT bothering the user, and only
 * then flips the SDK state with logout({ openUrl: false }) so the routing
 * layer sends the user to the login page. Transient failures (network down,
 * timeouts) must never log the user out.
 *
 * Why recover instead of logging out straight away: a dead refresh token is
 * not a dead session. Every day a dozen users lose their refresh token
 * family to Auth0's rotation reuse detection (tenant log `ferrt`) — the SPA
 * ends up presenting an already-rotated token, whatever the cause (lost
 * response, storage rolled back) — and many more lose the token to Safari
 * ITP eviction or the 15-day idle limit. In all of those the Auth0 SSO
 * session cookie is usually still alive, so a top-level redirect to
 * /authorize with prompt=none comes straight back with a fresh code and a
 * new refresh-token family, no form shown. Top-level (not iframe) so ITP
 * does not block the first-party Auth0 cookie. If the session is gone too,
 * Auth0 answers login_required on /auth and the login page is shown as
 * before. The attempt is rate-limited through sessionStorage (one redirect
 * per cooldown, the stamp is deliberately NOT cleared on success) so a
 * session that keeps dying — e.g. a full localStorage that can never persist
 * the new token — cannot bounce the user through Auth0 in a loop.
 */

export const RECHECK_INTERVAL_MS = 60_000;

/** sessionStorage key: epoch ms of the last silent-recovery redirect. */
export const SILENT_RECOVERY_KEY = 'auth0_silent_recovery_at';
/** Only one silent-recovery redirect per window; after that, log out. */
export const SILENT_RECOVERY_COOLDOWN_MS = 10 * 60_000;

// Error codes @auth0/auth0-spa-js surfaces when silent token acquisition can
// only be fixed by an interactive login. Anything else (fetch/timeout errors
// have no `error` code) is treated as transient.
const UNRECOVERABLE_CODES = new Set([
    'missing_refresh_token', // ITP/private mode evicted the refresh token
    'invalid_grant', // refresh token expired, revoked or rotated-out
    'login_required', // Auth0 session is gone
    'interaction_required',
    'consent_required',
]);

export function isUnrecoverableTokenError(e: unknown): boolean {
    if (!e || typeof e !== 'object') return false;
    const code = (e as { error?: unknown }).error;
    return typeof code === 'string' && UNRECOVERABLE_CODES.has(code);
}

/**
 * Error codes Auth0 returns to redirect_uri when a prompt=none /authorize
 * cannot complete without user interaction. They are the EXPECTED outcome of
 * a failed silent recovery, not something to alarm the user with.
 */
const SILENT_RECOVERY_OUTCOME_CODES = new Set([
    'login_required',
    'interaction_required',
    'consent_required',
]);

export function isSilentRecoveryOutcome(error: string | null | undefined): boolean {
    return typeof error === 'string' && SILENT_RECOVERY_OUTCOME_CODES.has(error);
}

// sessionStorage can throw (Safari "Block All Cookies", private mode). A
// throwing store must degrade to "no recovery attempted yet", never crash.
function readRecoveryStamp(): number | null {
    try {
        const raw = window.sessionStorage.getItem(SILENT_RECOVERY_KEY);
        const value = raw === null ? NaN : Number(raw);
        return Number.isFinite(value) ? value : null;
    } catch {
        return null;
    }
}

function writeRecoveryStamp(now: number): void {
    try {
        window.sessionStorage.setItem(SILENT_RECOVERY_KEY, String(now));
    } catch {
        /* storage unavailable — the cooldown simply won't be enforced */
    }
}

export function clearSilentRecoveryStamp(): void {
    try {
        window.sessionStorage.removeItem(SILENT_RECOVERY_KEY);
    } catch {
        /* storage unavailable — nothing to clear */
    }
}

/** True when a silent recovery may be attempted now (none in the cooldown). */
export function canAttemptSilentRecovery(now: number = Date.now()): boolean {
    const last = readRecoveryStamp();
    return last === null || now - last > SILENT_RECOVERY_COOLDOWN_MS;
}

type SilentRecoveryClient = {
    loginWithRedirect: ReturnType<typeof useAuth0>['loginWithRedirect'];
};

/**
 * Top-level prompt=none redirect through Auth0. Resolves once the redirect
 * has been issued; the page unloads right after.
 */
export async function startSilentRecovery(
    { loginWithRedirect }: SilentRecoveryClient,
    now: number = Date.now(),
): Promise<void> {
    writeRecoveryStamp(now);
    await loginWithRedirect({
        authorizationParams: {
            prompt: 'none',
            redirect_uri: window.location.origin + '/auth',
        },
        appState: {
            returnTo: window.location.pathname + window.location.search,
            silentRecovery: true,
        },
    });
}

export function AuthExpiryGuard(): null {
    const {
        isAuthenticated,
        isLoading,
        getAccessTokenSilently,
        loginWithRedirect,
        logout,
    } = useAuth0();
    const checking = useRef(false);

    useEffect(() => {
        if (isLoading || !isAuthenticated) return;
        let disposed = false;

        const check = async () => {
            if (checking.current) return;
            checking.current = true;
            try {
                await getAccessTokenSilently();
            } catch (e) {
                if (disposed || !isUnrecoverableTokenError(e)) return;
                if (canAttemptSilentRecovery()) {
                    try {
                        await startSilentRecovery({ loginWithRedirect });
                        return;
                    } catch {
                        /* could not even start the redirect — fall through */
                    }
                }
                await logout({ openUrl: false });
            } finally {
                checking.current = false;
            }
        };

        void check();
        const onFocus = () => void check();
        const onVisibility = () => {
            if (!document.hidden) void check();
        };
        window.addEventListener('focus', onFocus);
        document.addEventListener('visibilitychange', onVisibility);
        const timer = setInterval(() => void check(), RECHECK_INTERVAL_MS);
        return () => {
            disposed = true;
            window.removeEventListener('focus', onFocus);
            document.removeEventListener('visibilitychange', onVisibility);
            clearInterval(timer);
        };
    }, [isLoading, isAuthenticated, getAccessTokenSilently, loginWithRedirect, logout]);

    return null;
}
